/**
 * @license
 * Copyright (c) 2004 %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { CalendarUtils } from './CalendarUtils';
import { DateDef } from './ISODateTimezoneOffset';
import { FormatParseError } from '../UNSAFE_IntlFormatParse';
import { getISODateOffset } from './ISODateTimezoneOffset';
import { Logger } from '../utils/UNSAFE_logger';
import { NativeDateTimeConstants } from './NativeDateTimeConstants';
import { NativeDateTimeOptions } from './NativeDateTimeOptions';
import { NativeParserImpl } from './NativeParserImpl';
import { NativeDateTimeResolvedOptions } from './NativeDateTimeOptions';
import * as LocalOraI18nUtils from './dateTimeUtils';

/**
 * Create an Intl.DateTimeFormat from the options.
 */
export const getFormatter = (options: NativeDateTimeOptions) => {
  return new Intl.DateTimeFormat(options.locale, options);
};

/**
 * Get the resolved options from the Intl.DateTimeFormat instance, plus any custom options used by our implementation.
 */
export const getResolvedOptions = (
  formatter: Intl.DateTimeFormat,
  options: NativeDateTimeOptions
): NativeDateTimeResolvedOptions => {
  const resOptions = formatter.resolvedOptions();
  const isoStrFormat = options.isoStrFormat ?? 'auto';
  const twoDigitYearStart = options.twoDigitYearStart ?? 1950;
  const lenientParse = options.lenientParse ?? 'full';
  const patternFromOptions = getPatternFromOptions(formatter, resOptions);
  return { ...resOptions, isoStrFormat, twoDigitYearStart, lenientParse, patternFromOptions };
};

/**
 * Create an Intl.DateTimeFormat that can merge in the year in the desired format during the call to 'format'.
 * This is useful if you always want year: 'numeric' for dateStyle: 'short' even if
 * in some locales dateStyle: 'short' yields a 2-digit year. 2-digit years are ambiguous.
 */
export const getYearFormatter = (
  options: NativeDateTimeOptions,
  resOptions: NativeDateTimeResolvedOptions
) => {
  let formatter: Intl.DateTimeFormat | null = null;
  // Set up the yearFormatter if dateStyle is short and the dateStyleShortYear option is defined.
  // If we do not add numberingSystem/calendar, then Intl.DateTimeFormat defaults to the locale's
  // numberingSystem, and that is not what JET uses.
  if (options.dateStyle === 'short' && options.dateStyleShortYear) {
    formatter = new Intl.DateTimeFormat(options.locale, {
      year: options.dateStyleShortYear,
      numberingSystem: resOptions.numberingSystem,
      calendar: resOptions.calendar
    });
  }
  return formatter;
};

/**
 * Merge in the year in the desired format rather than whatever the locale default is for dateStyle: 'short'.
 */
export const formatWithYearFormat = (
  formatInstance: Intl.DateTimeFormat,
  yearInstance: Intl.DateTimeFormat,
  value: Date
) => {
  // first get the formatted numeric year
  const yearParts = yearInstance.formatToParts(value);
  const formattedYear = yearParts.find((part) => part.type === 'year')?.value;
  // next get the formatToParts of the formatInstance and substitute in formattedYear.
  const parts = formatInstance.formatToParts(value);
  return parts.reduce((acc, part) => {
    if (part.type === 'year') {
      return acc + (formattedYear ?? part.value);
    }
    return acc + part.value;
  }, '');
};

/**
 * Formats a date time string based on the given options.
 */
export const formatDateTimeWithOptions = (options: NativeDateTimeOptions, value: string) => {
  const intlFormatter = getFormatter(options);
  const resOptions = getResolvedOptions(intlFormatter, options);
  const yearFormatter = getYearFormatter(options, resOptions);
  return formatDateTime(intlFormatter, yearFormatter, resOptions.timeZone, value);
};

/**
 * Formats a date time string using Intl.DateTimeFormat.
 */
export const formatDateTime = (
  intlFormatter: Intl.DateTimeFormat,
  yearFormatter: Intl.DateTimeFormat | null,
  timeZone: string,
  value: string
) => {
  const isoStr = normalizeIsoString(timeZone, value);
  const isoStrDate = new Date(isoStr);
  return yearFormatter
    ? formatWithYearFormat(intlFormatter, yearFormatter, isoStrDate)
    : intlFormatter.format(isoStrDate);
};

/**
 * Parses a string into an iso string based on the given options.
 */
export const parseDateTimeWithOptions = (options: NativeDateTimeOptions, str: string) => {
  const intlFormatter = getFormatter(options);
  const resOptions = getResolvedOptions(intlFormatter, options);
  return parseDateTime(options.locale, intlFormatter, resOptions, str);
};

/**
 * Parses the formatted string, and returns an iso string.
 * @returns an iso string
 * @throws Error if undefined, null, or '' or not an iso string, or if something went wrong in the call to parse.
 */
export const parseDateTime = (
  locale: string,
  formatter: Intl.DateTimeFormat,
  resOptions: NativeDateTimeResolvedOptions,
  str: string
) => {
  if (str === undefined || str === null || str === '') {
    throw new FormatParseError('The parse value cannot be empty.', {
      cause: { code: 'emptyParseValue' }
    });
  }
  const cal = CalendarUtils.getCalendar(locale, resOptions.calendar);
  const pattern = getPatternFromOptions(formatter, resOptions);
  const result = NativeParserImpl.parseImpl(str, pattern, resOptions, cal);
  const parsed = result.value;
  if (parsed) {
    if (result.warning) {
      Logger.warn('The value ' + str + ' was leniently parsed to represent a date ' + parsed);
    }
  }
  return parsed;
};

/**
 * Checks if it is a valid iso string.
 * Also fixes up the iso string if needed, i.e. if local and timezone are specified in the options.
 * @param timeZone
 * @param value
 * @returns
 * @throws Error if undefined, null, or '' or if it is an invalid iso string.
 */
export const normalizeIsoString = (timeZone: string, value: string) => {
  if (value === undefined || value === null || value === '') {
    throw new FormatParseError('The format value cannot be empty.', {
      cause: { code: 'emptyFormatValue' }
    });
  }

  if (value.startsWith('T')) {
    // Intl Object takes Date object as parameter. new Date() throws an
    // exception with time only ISO string. Prepend current date to time
    // only iso string.
    let curDate = '';
    if (timeZone) {
      // if options contain a timeZone, get the current date at this time zone
      curDate = NativeParserImpl.getTimeZoneCurrentDate(timeZone);
    } else {
      // get the current date of the local system
      curDate = LocalOraI18nUtils.dateToLocalIso(new Date()).split('T')[0];
    }
    value = curDate + value;
  } else if (value.indexOf('T') === -1) {
    value = value + 'T00:00:00';
  }

  // test if value is a valid ISO string
  const exe = LocalOraI18nUtils._ISO_DATE_REGEXP.exec(value);
  if (!exe) {
    const e = new FormatParseError('The format value must be a valid iso string.', {
      cause: { code: 'invalidISOString', parameterMap: { isoStr: value } }
    });
    throw e;
  }
  // If value is local ISO string and timeZone is specified in options, adjust
  // offset to avoid time zone conversion from local system's timezone to target timezone
  if (timeZone) {
    // Also test if timeZone in the options is the same as local system time zone
    let islocalTimeZone = false;
    const localTimeZone = NativeParserImpl.getLocalSystemTimeZone();
    if (localTimeZone === timeZone) {
      islocalTimeZone = true;
    }
    const timePart = value.substring(value.indexOf('T'));
    const isLocalValue =
      timePart.indexOf('Z') === -1 && timePart.indexOf('+') === -1 && timePart.indexOf('-') === -1;
    if (isLocalValue && !islocalTimeZone) {
      const parts = LocalOraI18nUtils.IsoStrParts(value);
      const dateParts: DateDef = {
        year: parts[0],
        month: parts[1],
        date: parts[2],
        hours: parts[3],
        minutes: parts[4]
      };
      const offset = getISODateOffset(dateParts, timeZone);
      value = value + LocalOraI18nUtils.getTimeStringFromOffset('', offset, false, true);
    }
  }
  //if ISO string time zone offset is hh, replace it with hh:00 so that new Date() does not fail
  value = value.replace(/(T.*?[+-]..$)/, '$1:00');
  return value;
};

const getPatternFromOptions = (
  formatter: Intl.DateTimeFormat,
  options: NativeDateTimeResolvedOptions
) => {
  const isoSTr = '2000-01-02T00:00:00';
  const date = new Date(isoSTr);
  let pattern = '';
  let optVal = '';
  let token: any = null;
  let dtokenMap: any = null;
  let ttokenMap: any = null;
  let dateStyle = false;
  let timeStyle = false;
  if (options.dateStyle !== undefined) {
    dtokenMap = NativeDateTimeConstants._dateTimeFormats.dateStyle;
    dtokenMap = dtokenMap[options.dateStyle];
    dateStyle = true;
  }
  if (options.timeStyle !== undefined) {
    ttokenMap = NativeDateTimeConstants._dateTimeFormats.timeStyle;
    ttokenMap = ttokenMap[options.timeStyle];
    timeStyle = true;
  }
  const tokenMap = NativeDateTimeConstants._tokenMap;
  // eslint-disable-next-line array-callback-return
  formatter.formatToParts(date).map(({ type, value }) => {
    // TODO: Intl.DateTimeFormatPartType type does not have 'fractionalSecond' even though it is in the api.
    // so to workaround getting a typescript error, I'm setting as string here.
    // Fix when the typescript for Intl.DateTimeFormat gets fixed.
    switch (type as string) {
      case 'literal':
        token = value.replace(NativeDateTimeConstants._ALPHA_REGEXP, "'$1'");
        break;
      case 'dayPeriod':
        token = 'a';
        break;
      case 'hour':
        if (timeStyle) {
          token = ttokenMap[type];
        } else {
          optVal = (options as any)[type];
          token = (tokenMap as any)[type][optVal];
        }
        let ish12 = options.hour12;
        if (ish12 === undefined) {
          ish12 = false;
        }
        // take hourCycle into consideration
        if (options.hourCycle) {
          token = token.replace(
            NativeDateTimeConstants._HOUR12_REGEXP,
            NativeDateTimeConstants._hourCycleMap[options.hourCycle]
          );
        }
        // h12 takes precedence over hourCycle
        if (ish12 === true) {
          token = token.replace(NativeDateTimeConstants._HOUR12_REGEXP, 'h');
        }
        break;
      case 'month':
        // some locales have month as numeric for medium style and some have month
        // as medium style check the value to get accurate pattern
        if (dateStyle) {
          if (isNaN(+value)) {
            token = dtokenMap.month_m;
          } else {
            token = dtokenMap.month_s;
          }
        } else {
          optVal = (options as any)[type];
          token = (tokenMap as any)[type][optVal];
        }
        break;
      case 'year':
      case 'day':
      case 'weekday':
        if (dateStyle) {
          token = dtokenMap[type];
        } else {
          optVal = (options as any)[type];
          token = (tokenMap as any)[type][optVal];
        }
        break;
      case 'minute':
      case 'second':
      case 'timeZoneName':
        if (timeStyle) {
          token = ttokenMap[type];
        } else {
          optVal = (options as any)[type];
          token = (tokenMap as any)[type][optVal];
        }
        break;
      case 'era':
        optVal = (options as any)[type] || 'short';
        token = (tokenMap as any)[type][optVal];
        break;
      case 'fractionalSecond':
        token = options.fractionalSecondDigits;
        token = (tokenMap as any)[type][token];
        break;
      default:
        break;
    }
    pattern += token;
  });
  return pattern;
};
